import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Avatar,
  Box,
  Card,
  CardContent,
  CardOverflow,
  Stack,
  Typography,
} from "@mui/joy";
import { ApiQuestDetail } from "griptonite-api-client/types";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import AppIcon from "@/assets/icons/AppIcon";
import { useQuestQuery, useUpdateQuestMutation } from "@/hooks/quests";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { MediaItemPreview, MediaSelectInput } from "../forms/MediaInput";
import SelectInput from "../forms/SelectInput";
import TextInput, { TextareaInput } from "../forms/TextInput";
import BottomButtons from "./BottomButtons";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";
import {
  QuestNotificationName,
  QuestPromotionFormSchema,
  QuestPromotionFormValues,
  questPromotionFormValuesToPayload,
  questToPromotionFormValues,
} from "./promotionForms";
import QuestProfileTabs from "./QuestProfileTabs";
import QuestTabLayout from "./QuestTabLayout";

// The API's own texts, used whenever the gym leaves one blank. The ending soon text's placeholders
// are filled in per climber.
const DEFAULT_TEXTS: Record<QuestNotificationName, string> = {
  questStart: "Earn credits for climbing. Tap for more info",
  questEndingSoon:
    "You're {{POINTS}} points from earning {{CREDITS}} credits. Tap to see your progress",
  checkinPromo: "Join this quest to earn credits for climbing.",
};

// The quest's settings only come with its detail, so the form waits for it.
export default function QuestPromotion({ questUuid }: { questUuid: string }) {
  const routeVenueUuid = useCurrentVenueUuid();
  const { data: quest } = useQuestQuery(questUuid, routeVenueUuid);

  if (!quest || !("settings" in quest)) {
    return null;
  }
  return <QuestPromotionForm quest={quest} routeVenueUuid={routeVenueUuid} />;
}

const QuestPromotionForm = ({
  quest,
  routeVenueUuid,
}: {
  quest: ApiQuestDetail;
  routeVenueUuid?: string;
}) => {
  const updateMutation = useUpdateQuestMutation(quest.uuid, routeVenueUuid);
  useUnhidePreviousTabWarningsOnMount(quest.uuid, "rules");

  const defaultValues = useMemo(() => questToPromotionFormValues(quest), [quest]);

  const methods = useForm<QuestPromotionFormValues>({
    mode: "onBlur",
    resolver: zodResolver(QuestPromotionFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmitBase = async (values: QuestPromotionFormValues, isPublish: boolean) => {
    await updateMutation.mutateAsync(
      {
        ...questPromotionFormValuesToPayload(values, defaultValues),
        ...(isPublish ? { isHidden: false } : {}),
      },
      {
        onError: (error) => handleFormError(error, methods),
      },
    );
  };

  const onSubmit = (values: QuestPromotionFormValues) => onSubmitBase(values, false);
  const onPublish = (values: QuestPromotionFormValues) => onSubmitBase(values, true);

  return (
    <FormProvider {...methods}>
      <QuestTabLayout>
        <QuestProfileTabs questUuid={quest.uuid} />
        <Stack
          sx={{ padding: 1.5, gap: 1.5, flex: 1, overflowY: "auto", minHeight: 0 }}
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <FormErrorAlert />
          <PromotionSections quest={quest} />
          <BottomButtons
            quest={quest}
            step="promotion"
            formState={methods.formState}
            onPublishClick={() => methods.handleSubmit(onPublish)()}
          />
        </Stack>
      </QuestTabLayout>
    </FormProvider>
  );
};

const PromotionSections = ({ quest }: { quest: ApiQuestDetail }) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>("notifications");

  return (
    <AccordionGroup
      size="lg"
      sx={{
        flex: 1,
        overflow: "hidden",
        gap: 1,
        padding: 0,
        ".MuiAccordion-root.Mui-expanded": {
          flex: 1,
        },
        ".MuiAccordion-root": {
          border: "none",
          padding: 0,
        },
        ".MuiAccordionSummary-root": {
          marginRight: 0,
          marginLeft: 0,
          border: "none",
        },
        ".MuiAccordionDetails-content.Mui-expanded": {
          overflowY: "auto",
          paddingTop: 2,
        },
        ".MuiAccordionSummary-button": {
          fontWeight: "bold",
          backgroundColor: "neutral.100",
          borderRadius: 8,
        },
      }}
    >
      <Accordion
        expanded={expandedAccordion === "notifications"}
        onChange={() => setExpandedAccordion("notifications")}
      >
        <AccordionSummary>
          <Typography level="title-md">Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <NotificationsForm quest={quest} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "tv-display"}
        onChange={() => setExpandedAccordion("tv-display")}
      >
        <AccordionSummary>
          <Typography level="title-md">TV advertising</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TvDisplayForm />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "feed-card"}
        onChange={() => setExpandedAccordion("feed-card")}
      >
        <AccordionSummary>
          <Typography level="title-md">Feed card</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FeedCardForm quest={quest} />
        </AccordionDetails>
      </Accordion>
    </AccordionGroup>
  );
};

const NotificationsForm = ({ quest }: { quest: ApiQuestDetail }) => {
  const { data: venue } = useCurrentVenueQuery();
  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
      <NotificationCard
        name="questStart"
        title="Starting notification"
        previewTitle={`${venue?.name || "Gym"} - ${quest.name}`}
        description="Sent to recent visitors to promote the quest just after it starts"
        disabled={quest.status !== "UPCOMING"}
      />
      <NotificationCard
        name="checkinPromo"
        title="Check-in promo"
        previewTitle={quest.name}
        description="Shown in the app to customers at the gym who haven't joined the quest"
      />
      {/* Only points quests send it, since it counts down to the next milestone. */}
      {quest.type === "LEADERBOARD_POINTS" ? (
        <NotificationCard
          name="questEndingSoon"
          title="Ending soon notification"
          previewTitle={`${quest.name} ends soon`}
          description="Sent five days before the quest ends, telling participants how close they are to their next milestone. {{POINTS}} and {{CREDITS}} are filled in for each participant."
        />
      ) : null}
    </Box>
  );
};

const NotificationCard = ({
  name,
  title,
  description,
  previewTitle,
  disabled,
}: {
  name: QuestNotificationName;
  title: string;
  description: string;
  previewTitle: string;
  disabled?: boolean;
}) => {
  const { watch } = useFormContext<QuestPromotionFormValues>();
  const enabled = watch(`notifications.${name}.enabled`);

  return (
    <Card sx={{ minWidth: 200 }}>
      <CardContent>
        <Typography level="title-md" marginBottom={0}>
          {title}
        </Typography>
        <NotificationPreview name={name} title={previewTitle} />
        <Typography level="body-sm" textColor="neutral.600" marginTop={0.5}>
          {description}
        </Typography>
        <Stack spacing={2} sx={{ marginTop: 1.5 }}>
          <Box>
            <CheckboxInput
              name={`notifications.${name}.enabled`}
              label="Enabled"
              disabled={disabled}
            />
          </Box>
          <TextareaInput
            name={`notifications.${name}.text`}
            label="Message"
            placeholder={DEFAULT_TEXTS[name]}
            minRows={2}
            maxRows={3}
            disabled={!enabled || disabled}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const NotificationPreview = ({ name, title }: { name: QuestNotificationName; title: string }) => {
  const { watch } = useFormContext<QuestPromotionFormValues>();
  const text = (watch(`notifications.${name}.text`) || DEFAULT_TEXTS[name])
    .replace("{{POINTS}}", "250")
    .replace("{{CREDITS}}", "10");

  return (
    <Stack
      spacing={1}
      padding={1.5}
      direction="row"
      sx={{
        backgroundColor: "#0009",
        borderRadius: 16,
        color: "white",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      }}
    >
      <AppIcon sx={{ width: 36, height: 36 }} />
      <Box>
        <Typography
          fontSize="13px"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
          textColor="white"
          lineHeight="1.2"
          marginBottom={0.25}
        >
          {title}
        </Typography>
        <Typography
          level="body-sm"
          fontSize="13px"
          fontFamily="system-ui, sans-serif"
          lineHeight="1.2"
          textColor="white"
        >
          {text}
        </Typography>
      </Box>
    </Stack>
  );
};

const FREQUENCY_OPTIONS = [
  { name: "Low", value: "LOW", description: "Shown about 2 times per hour" },
  { name: "Medium", value: "MEDIUM", description: "Shown about 4 times per hour" },
  { name: "High", value: "HIGH", description: "Shown about 12 times per hour" },
];

const TvDisplayForm = () => {
  const { watch } = useFormContext<QuestPromotionFormValues>();
  const frequency = watch("tvTakeover.frequency");
  const frequencyDescription = FREQUENCY_OPTIONS.find(
    (opt) => opt.value === frequency,
  )?.description;

  return (
    <Stack spacing={1}>
      <Box>
        <CheckboxInput name="tvTakeover.enabled" label="Enable TV ad for this quest" />
      </Box>
      <MediaSelectInput
        name="tvTakeover.media"
        label="Media (image or video)"
        accept="image/*,video/*"
        multiple={false}
        helpText="Upload an image or video that will be displayed on gym TV screens to promote this quest."
      />
      <SelectInput
        name="tvTakeover.frequency"
        label="Display frequency"
        options={FREQUENCY_OPTIONS}
      />
      <Typography level="body-sm" textColor="neutral.600">
        {frequencyDescription}
      </Typography>
    </Stack>
  );
};

const FeedCardForm = ({ quest }: { quest: ApiQuestDetail }) => (
  <Stack direction="row" spacing={2}>
    <Stack spacing={1}>
      <Box>
        <CheckboxInput name="feedCard.enabled" label="Enable feed card for this quest" />
      </Box>
      <TextInput name="feedCard.headline" label="Custom headline" placeholder={quest.name} />
      <TextareaInput
        name="feedCard.text"
        label="Custom post text"
        placeholder={quest.description ?? undefined}
        minRows={2}
        maxRows={4}
      />
      <MediaSelectInput
        name="feedCard.image"
        label="Image"
        accept="image/*"
        multiple={false}
        helpText="Upload an image that will be displayed in the feed card to promote this quest. If not set, the quest cover image will be used"
      />
    </Stack>
    <FeedCardPreview quest={quest} />
  </Stack>
);

// Blank fields fall back to the quest's own name, description and cover image, as in the app.
const FeedCardPreview = ({ quest }: { quest: ApiQuestDetail }) => {
  const { watch } = useFormContext<QuestPromotionFormValues>();
  const headline = watch("feedCard.headline");
  const text = watch("feedCard.text");
  const image = watch("feedCard.image") || quest.coverImage;
  const { data: venue } = useCurrentVenueQuery();

  return (
    <Card sx={{ maxWidth: 400, minWidth: 320, alignSelf: "flex-start" }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, flex: "0" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            src={venue?.logo?.url || ""}
            size="md"
            sx={{
              borderRadius: 4,
              borderColor: "neutral.200",
              borderWidth: 1,
              borderStyle: "solid",
            }}
          />
          <Box>
            <Typography fontWeight="bold" level="body-sm">
              {venue?.name || "Gym"}
            </Typography>
            <Typography level="body-sm" textColor="neutral.600">
              Just now
            </Typography>
          </Box>
        </Stack>
        <Typography level="body-sm">{text || quest.description}</Typography>
      </CardContent>
      <CardOverflow
        sx={{
          background: "radial-gradient(91.33% 55.37% at 8.67% 7.9%, #1E1C54 0%, #161541 100%)",
          paddingBottom: 2,
        }}
      >
        <Typography
          fontSize="24px"
          textAlign="center"
          padding={2}
          textColor="white"
          fontWeight="bold"
          lineHeight={1.25}
        >
          {headline || quest.name}
        </Typography>
        {image ? (
          <Box sx={{ width: "100%" }}>
            <MediaItemPreview mediaItem={image} />
          </Box>
        ) : (
          <Box
            sx={{
              aspectRatio: 2,
              border: "2px solid",
              borderColor: "neutral.200",
              display: "flex",
            }}
          />
        )}
      </CardOverflow>
    </Card>
  );
};
